import { isAxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { LOGIN_TYPE, ROLES } from '../assets/data/common';
import { fetchAccount, getAccountByIdentity, getAccountInfo, getAccountList } from '../axios/Account/AccountApi';
import { getCompany, switchCompany } from '../axios/Company/CompanyApi';
import { getConfigsLogin, getSsoUserInfo, logoutRequest } from '../axios/Login/loginApi';
import { useAuth as useAuthSSO } from "react-oidc-context";
import {
   getAccessToken,
   getTypeAccessToken,
   removeAccessToken,
   setAccessToken,
   setHeaderCompanyId,
} from '../utils/Function/commonFunction';
import { useLoading } from './LoadingContext';
import Cookies from 'js-cookie';
import { getListEntitieById, getListEntitieByUserId } from '../axios/Setting/SettingUrlApi';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axiosBasicInstance from '../axios/axiosConfig';
import { listUserAdmin } from '../axios/Notification/NotificationApi';
import ConfirmWantToContinueDialog from '../components/ConfirmWantToContinueDialog';
import AppBootstrapLoader from '../components/AppBootstrapLoader';
import { SpinnerRing } from '../components/CustomSpinner';

const INIT_DATA = {
   profile: {
      accountId: '',
      firstName: '',
      lastName: '',
      company: '',
      comment: '',
      email: '',
      role: '',
      city: '',
      country: '',
      state: '',
      zipCode: '',
      mobile: '',
      telephone: '',
      profilePicture: '',
      citizenIdentificationNumber: '',
      createdTime: '',
      modifiedTime: '',
      status: false,
      companyId: null,
      userId: null,
      companyLogo: '',
   },
   isAuthenticated: false,
   isReady: false,
   isNormalLogin: true,
   isAdmin: false,
   isEmployee: false,
   isAuditor: false,
   isSubscription: false,
   ownerId: null,
   login: (accessToken, type) => { },
   logout: () => { },
   refreshProfile: () => { },

};

const AUTH_ROUTES = {
   login: '/login',
   signup: '/sign-up',
   forgotPassword: '/forgot-password',
   reset: '/reset',
   invitation: '/invitation',
   signupOrganization: '/sign-up',
   signupOrganizationSSO: '/sign-up/organization/authentication',
   signupSuccess: '/sign-up/success',
   refreshToken: '/refreshToken',
};

/** API đăng ký/đăng nhập — 401 không được coi là hết session để logout SSO */
const AUTH_API_PATHS = [
   '/signup',
   '/signup-business',
   '/sso-login',
   '/refreshToken',
   '/login',
];

const ROUTES = Object.values(AUTH_ROUTES);

const LAST_ROUTE_COOKIE = "lastVisitedRoute";

const INACTIVITY_MS = 10 * 60 * 1000;
const WARNING_BEFORE_MS = 3 * 60 * 1000;
/** Khóa exp refresh token lúc login — silent renew không kéo dài bộ đếm session */
const SESSION_REFRESH_EXP_LOCK_KEY = "fs_session_refresh_exp_ms";

const PUBLIC_AUTH_ROUTES = [
   "/login",
   "/sign-up",
   "/invitation",
   "/reset",
   "/forgot-password",
];

const isPublicAuthRoute = (pathname) =>
   pathname === "/" || PUBLIC_AUTH_ROUTES.includes(pathname);

const isPageVisible = () =>
   typeof document !== "undefined" && !document.hidden;

const AuthContext = createContext(INIT_DATA);

const mapSsoUserInfo = (data) => {
   const givenName = (data?.given_name || '').trim();
   const familyName = data?.family_name || '';
   const firstName = givenName ? givenName.split(' ').slice(-1)[0] : '';
   const lastName = `${givenName.split(' ').slice(0, -1).join(' ')} ${familyName}`.trim();

   return {
      firstName,
      lastName,
      email: data?.email || '',
      name: data?.name,
      preferred_username: data?.preferred_username,
      citizenNumber: data?.preferred_username || '',
   };
};

export const AuthProvider = ({ children }) => {
   const { t, i18n } = useTranslation();
   const location = useLocation();
   const navigate = useNavigate();
   const [show, hide] = useLoading();
   const [profile, setProfile] = useState(INIT_DATA.profile);
   const [isAuthenticated, setIsAuthenticated] = useState(INIT_DATA.isAuthenticated);
   const [isReady, setIsReady] = useState(false);
   // Chỉ dùng cho màn hình loading lúc login / khởi tạo session — không ảnh hưởng LoadingProvider từng component
   const [isAuthBootstrapping, setIsAuthBootstrapping] = useState(true);
   const [refreshCount, setRefreshCount] = useState(0);
   const [refreshCountSetting, setRefreshCountSetting] = useState(0);
   const isNormalLogin = getTypeAccessToken() === LOGIN_TYPE.normal;
   const isAdmin = profile.role === ROLES.Admin;
   const isEmployee = profile.role === ROLES.Employee;
   const isAuditor = profile.role === ROLES.Auditor;
   const isSubscription = profile.role === ROLES.Subscription;
   const auth = useAuthSSO()
   const [dataConfig, setDataConfig] = useState([]);
   const [userSignUp, setUserSignUp] = useState([])
   const [infoUserSignUp, setInfoUserSignUp] = useState([])
   const currentLang = i18n.language;
   const [dashboardRefreshInterval, setDashboardRefreshInterval] = useState(0);
   const [showInactivityDialog, setShowInactivityDialog] = useState(false);
   const inactivityTimerRef = React.useRef(null);
   const lastActivityRef = React.useRef(Date.now());
   /** Tránh gọi login()/bootstrap lại khi silent renew cùng hoặc đổi access token */
   const lastSyncedAccessTokenRef = React.useRef(null);
   /** Exp (ms) của refresh token lần login đầu — không đổi khi silent renew */
   const sessionRefreshExpMsRef = React.useRef(
      Number(sessionStorage.getItem(SESSION_REFRESH_EXP_LOCK_KEY)) || null
   );
   const tRef = React.useRef(t);
   tRef.current = t;

   const { pathname } = useLocation();
   // const [manualLogout, setManualLogout] = useState(false);
   const [checkingToken, setCheckingToken] = useState(true);
   const [statusSwitch, setStatusSwitchState] = useState(false);
   const statusSwitchRef = React.useRef(false);
   /** Sau update company logo, một lần fetch account tiếp theo không ghi đè logo (API account hay trễ) */
   const skipAccountLogoOverwriteRef = React.useRef(false);
   const [departmentSatus, setDepartmentSatus] = useState(false);

   // Đồng bộ ref để effect refreshProfile luôn đọc đúng trạng thái switch company
   const setStatusSwitch = useCallback((value) => {
      statusSwitchRef.current = value;
      setStatusSwitchState(value);
   }, []);

   const setCookie = (
      name,
      value,
      options = {}
   ) => {
      if (options.expires) {
         options.expires = options.expires.toUTCString();
      }
      let updatedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(
         value
      )}`;
      for (const [key, val] of Object.entries(options)) {
         updatedCookie += `; ${key}`;
         if (val !== true) {
            updatedCookie += `=${val}`;
         }
      }
      document.cookie = updatedCookie;
   };

   const [genaralSettingDateFormat, setGenaralSettingDateFormat] = useState("");
   const [genaralSettingTimeFormat, setGenaralSettingTimeFormat] = useState("");
   const [genaralSettingTimeZone, setGenaralSettingTimeZone] = useState("");
   const [genaralExpiredTime, setGenaralExpiredTime] = useState("");
   const [weekStartDay, setWeekStartDay] = useState("");

   const [dateTimeFormat, setDateTimeFormat] = useState("");
   const [listAccountCompany, setListAccountCompany] = useState([])
   const [leaveTypeDeducted, setLeaveTypeDeducted] = useState("");

   const [autoApproveTime, setAutoApproveTime] = useState("");
   const [defaultReason, setDefaultReason] = useState("");
   const [leaveToBeDeducted, setLeaveToBeDeducted] = useState("");
   const [numberOfHoursOneDay, setNumberOfHoursOneDay] = useState("");
   const [maximumLeaveDuration, setMaximumLeaveDuration] = useState("");
   const [automaticallyConvertUnexcusedLeaveToExcusedLeave, setAutomaticallyConvertUnexcusedLeaveToExcusedLeave] = useState(false);
   const [leaveTypeWithoutPermission, setLeaveTypeWithoutPermission] = useState("");
   const [ownerId, setOwnerId] = useState(INIT_DATA.ownerId);

   function getCookie(name) {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; ${name}=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
   }

   const accessToken = getCookie('accessToken') ? getCookie('accessToken') : ''
   useEffect(() => {
      const getGeneralSettings = async () => {
         try {
            // --- Entity ID = 2 ---
            const resEntity2 = await getListEntitieById({ entityId: 2, langId: localStorage.getItem("i18nextLngId") });
            const settings2 = resEntity2.data?.data || [];

            const dateFormat = settings2.find(s => s.settingEntityTypeNameEN === "Date format")?.value;
            const timeFormat = settings2.find(s => s.settingEntityTypeNameEN === "Time format")?.value;
            const timzone = settings2.find(s => s.settingEntityTypeNameEN === "Timezone")?.value;
            const expiredTimeCheck = settings2.find(s => s.settingEntityTypeNameEN === "Timekeeping request expiration time")?.value;
            const startDay = settings2.find(s => s.settingEntityTypeNameEN === "Week start date")?.value;
            const leaveTypeDeducted = settings2.find(s => s.settingEntityTypeNameEN === "The leave type to be deducted")?.value;
            const numberOfHoursOneDay = settings2.find(s => s.settingEntityTypeNameEN === "Time equivalent to one day of unauthorized absence")?.value;
            // BE: "Maximum Leave Duration" (Title Case) — không dùng "Maximum leave duration"
            const maximumLeaveDuration = settings2.find(s => s.settingEntityTypeNameEN === "Maximum leave duration")?.value;
            const automaticallyConvertRaw = settings2.find(
               (s) =>
                  s.settingEntityTypeNameEN ===
                  "Automatically convert unexcused leave to excused leave"
            )?.value;
            const leaveTypeWithoutPermission = settings2.find(
               (s) => s.settingEntityTypeNameEN === "Excluded leave types"
            )?.value;

            setGenaralSettingDateFormat(dateFormat);
            setGenaralSettingTimeFormat(timeFormat);
            // Timezone setting là offset (+07:00); fallback tránh "" khiến dayjs/luxon throw
            setGenaralSettingTimeZone(timzone || "+00:00");
            setGenaralExpiredTime(expiredTimeCheck);
            setWeekStartDay(startDay);
            setLeaveToBeDeducted(leaveTypeDeducted);
            setNumberOfHoursOneDay(numberOfHoursOneDay);
            setMaximumLeaveDuration(maximumLeaveDuration);
            setAutomaticallyConvertUnexcusedLeaveToExcusedLeave(
               automaticallyConvertRaw === "1" ||
                  automaticallyConvertRaw === 1 ||
                  automaticallyConvertRaw === true ||
                  automaticallyConvertRaw === "true"
            );
            setLeaveTypeWithoutPermission(leaveTypeWithoutPermission ?? "");
            // --- Entity ID = 5 ---
            const resEntity5 = await getListEntitieById({ entityId: 5, langId: localStorage.getItem("i18nextLngId") });
            const settings5 = resEntity5.data?.data || [];

            const dashboardRefreshInterval = settings5.find(s => s.settingEntityTypeNameEN === "Dashboard refresh interval")?.value;

            setDashboardRefreshInterval(dashboardRefreshInterval.replace(' minutes', ''));

            // --- Entity ID = 8 ---
            const resEntity8 = await getListEntitieById({ entityId: 8, langId: localStorage.getItem("i18nextLngId") });
            const settings8 = resEntity8.data.data[0]?.value || [];

            const departmentSatusGet = settings8 === "1" ? true : false;

            setDepartmentSatus(departmentSatusGet);

         } catch (error) {
            console.log(error);
         }
      };

      if (accessToken && profile.companyId) {
         getGeneralSettings();
      }
   }, [accessToken, pathname, refreshCountSetting, profile.companyId]);

   useEffect(() => {
      const getUserGeneralSettings = async () => {
         try {
            // --- Entity ID = 2 ---
            const resEntity3 = await getListEntitieByUserId({ entityId: 3, userId: profile.userId, langId: localStorage.getItem("i18nextLngId") });
            const settings3 = resEntity3.data?.data || [];

            const autoapprovetime = settings3.find(s => s.settingEntityTypeNameEN === "Automatic leave approval time")?.value;
            const defaultreason = settings3.find(s => s.settingEntityTypeNameEN === "Default content for leave approval reasons")?.value;

            setAutoApproveTime(autoapprovetime);
            setDefaultReason(defaultreason);

         } catch (error) {
            console.log(error);
         }
      };

      if (accessToken && profile.companyId && profile.userId) {
         getUserGeneralSettings();
      }
   }, [accessToken, pathname, refreshCountSetting, profile.companyId, profile.userId]);

   useEffect(() => {
      let cancelled = false;

      const fetchCompanyOwner = async () => {
         try {
            const response = await getCompany(
               profile.companyId,
               localStorage.getItem("i18nextLngId")
            );
            if (cancelled) return;

            const list = response?.data?.data;
            const company = Array.isArray(list) ? list[0] : list;
            setOwnerId(company?.ownerId ?? null);
         } catch (error) {
            if (!cancelled) {
               console.log(error);
               setOwnerId(null);
            }
         }
      };

      if (accessToken && profile.companyId) {
         setOwnerId(null);
         fetchCompanyOwner();
      } else {
         setOwnerId(null);
      }

      return () => {
         cancelled = true;
      };
   }, [accessToken, profile.companyId]);


   useEffect(() => {
      if (genaralSettingDateFormat && genaralSettingTimeFormat) {
         const formatString = `${genaralSettingDateFormat} ${genaralSettingTimeFormat}`;
         setDateTimeFormat(formatString)
      }
   }, [genaralSettingDateFormat, genaralSettingTimeFormat]);

   // Ở trên bạn đã có: const location = useLocation();
   useEffect(() => {
      if (!isAuthenticated || !profile.companyId) return;

      const currentPath = location.pathname;

      const AUTH_ONLY_ROUTES = [
         "/login",
         "/sign-up",
         "/invitation",
         "/reset",
      ];

      const isAuthRoute =
         currentPath === "/" || AUTH_ONLY_ROUTES.includes(currentPath);

      if (isAuthRoute) return;

      localStorage.setItem(LAST_ROUTE_COOKIE, currentPath);
   }, [isAuthenticated, profile.companyId, location.pathname]);


   const BAD_REQUEST_TOAST_ID = 'bad-request-error';

   const formatSeconds = (totalSeconds) => {
      const m = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
      const s = String(totalSeconds % 60).padStart(2, "0");
      return `${m}:${s}`;
   };

   const lockSessionRefreshExpMs = useCallback((expMs) => {
      if (!expMs || Number.isNaN(expMs)) return sessionRefreshExpMsRef.current;
      // Chỉ khóa lần đầu — silent renew / RT mới không kéo dài session countdown
      if (!sessionRefreshExpMsRef.current) {
         sessionRefreshExpMsRef.current = expMs;
         sessionStorage.setItem(SESSION_REFRESH_EXP_LOCK_KEY, String(expMs));
      }
      return sessionRefreshExpMsRef.current;
   }, []);

   const clearSessionRefreshExpLock = useCallback(() => {
      sessionRefreshExpMsRef.current = null;
      sessionStorage.removeItem(SESSION_REFRESH_EXP_LOCK_KEY);
   }, []);

   const readRefreshTokenExpMs = useCallback(() => {
      try {
         const raw = localStorage.getItem(dataConfig?.key_session_storage);
         if (!raw) return null;
         const session = JSON.parse(raw);
         const rt = session?.refresh_token;
         if (!rt) return null;
         const { exp } = jwtDecode(rt);
         return exp * 1000;
      } catch {
         return null;
      }
   }, [dataConfig?.key_session_storage]);

   // Bộ đếm session theo exp refresh token (khóa lúc login) — không reset khi đổi route / silent renew
   useEffect(() => {
      const toastId = "session-countdown-toast";

      if (!isAuthenticated) {
         return;
      }

      const tokenExpMs = readRefreshTokenExpMs();
      if (!tokenExpMs) return;

      const expMs = lockSessionRefreshExpMs(tokenExpMs);
      if (!expMs) return;

      const delta = expMs - Date.now();
      if (delta <= 0) {
         logoutNotFoundAccessToken();
         return;
      }

      let logoutTimer;
      let warnTimer;
      let countdownInterval;

      const startCountdownToast = () => {
         const tick = () => {
            // Trang public: ẩn toast nhưng vẫn giữ timer logout theo exp
            if (isPublicAuthRoute(window.location.pathname)) {
               toast.dismiss(toastId);
               return;
            }

            const msLeft = expMs - Date.now();
            const secondsLeft = Math.max(0, Math.floor(msLeft / 1000));

            if (secondsLeft <= 0) {
               if (countdownInterval) {
                  clearInterval(countdownInterval);
                  countdownInterval = null;
               }
               toast.update(toastId, {
                  render: `${tRef.current("common.want-to-continue")}`,
                  type: "error",
                  autoClose: 5000,
               });
               return;
            }

            const message = `${tRef.current("common.session-will-expire-in")} ${formatSeconds(secondsLeft)}`;

            if (toast.isActive(toastId)) {
               toast.update(toastId, { render: message });
            } else {
               toast.info(message, {
                  toastId,
                  autoClose: false,
                  closeOnClick: false,
               });
            }
         };

         tick();
         countdownInterval = setInterval(tick, 1000);
      };

      const timeLeftMs = expMs - Date.now();

      if (timeLeftMs <= WARNING_BEFORE_MS) {
         startCountdownToast();
      } else {
         const warnDelay = timeLeftMs - WARNING_BEFORE_MS;
         warnTimer = setTimeout(() => {
            startCountdownToast();
         }, warnDelay);
      }

      logoutTimer = setTimeout(() => {
         logoutNotFoundAccessToken();
         toast.dismiss(toastId);
      }, Math.max(0, expMs - Date.now()));

      return () => {
         if (logoutTimer) clearTimeout(logoutTimer);
         if (warnTimer) clearTimeout(warnTimer);
         if (countdownInterval) clearInterval(countdownInterval);
         toast.dismiss(toastId);
      };
      // Không phụ thuộc location.pathname / t — tránh reset bộ đếm khi đổi trang
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [isAuthenticated, dataConfig?.key_session_storage, readRefreshTokenExpMs, lockSessionRefreshExpMs]);

   useEffect(() => {
      const um = auth?.userManager;
      if (!um) return;

      const onSilentError = (err) => {
         console.error('Silent renew error:', err);
         // các thông điệp phổ biến khi refresh_token hết hạn
         const msg = String(err?.message || '').toLowerCase();
         if (
            msg.includes('invalid_grant') ||
            msg.includes('expired') ||
            msg.includes('refresh') ||
            msg.includes('token') ||
            msg.includes('session')
         ) {
            logoutNotFoundAccessToken();
         }
      };

      const onAccessTokenExpired = () => {
         console.warn('Access token expired');
      };

      const onUserSignedOut = () => {
         console.warn('User signed out at OP');
         logoutNotFoundAccessToken();
      };

      um.events.addSilentRenewError(onSilentError);
      um.events.addAccessTokenExpired(onAccessTokenExpired);
      um.events.addUserSignedOut(onUserSignedOut);

      return () => {
         um.events.removeSilentRenewError(onSilentError);
         um.events.removeAccessTokenExpired(onAccessTokenExpired);
         um.events.removeUserSignedOut(onUserSignedOut);
      };
   }, [auth?.userManager]);

   axiosBasicInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
         if (error.code === 'ERR_NETWORK') {
            toast.error(t('common.content-001'));
            return Promise.reject(error);
         }

         const url = error.config?.url || '';
         const body = (error.response?.data && JSON.stringify(error.response.data).toLowerCase()) || '';
         const isTokenEndpoint =
            url.includes('/protocol/openid-connect/token') ||
            url.includes('/sso-login') ||
            url.includes('/token');

         if (isTokenEndpoint && (body.includes('invalid_grant') || body.includes('expired') || body.includes('refresh'))) {
            await logoutNotFoundAccessToken();
            return Promise.reject(error);
         }

         const isAuthRoute =
            ROUTES.some((route) => url.includes(route)) ||
            AUTH_API_PATHS.some((path) => url.includes(path));
         // Đang ở trang signup/login công khai → không logout SSO khi API trả 401
         const pathname =
            typeof window !== 'undefined' ? window.location.pathname || '' : '';
         const isOnPublicAuthPage = PUBLIC_AUTH_ROUTES.some(
            (route) =>
               pathname === route ||
               pathname.endsWith(route) ||
               pathname.includes(`${route}/`)
         );
         // Account/User not found: không logout SSO — để fetchAccountInfo điều hướng /sign-up
         const isUserNotFound =
            body.includes('user not found') ||
            body.includes('account is not found') ||
            body.includes('"responsecode":1049') ||
            body.includes('"responsecode": 1049') ||
            (typeof error.response?.data === 'object' &&
               (String(error.response?.data?.responseCode) === '1049' ||
                  String(error.response?.data?.message || '')
                     .toLowerCase()
                     .includes('user not found') ||
                  String(error.response?.data?.message || '')
                     .toLowerCase()
                     .includes('account is not found')));

         // 401 trên API nghiệp vụ → logout (chỉ 1 lần, tránh spam khi nhiều request 401 song song)
         if (
            error.response?.status === 401 &&
            !isAuthRoute &&
            !isOnPublicAuthPage &&
            !isUserNotFound
         ) {
            try { await logoutNotFoundAccessToken(); } catch (_) { }
            return Promise.reject(error);
         }

         return Promise.reject(error);
      }
   );

   async function getUserInfoFromToken(token, options = {}) {
      const silent = options.silent === true;
      try {
         if (!silent) show();
         const { data } = await getSsoUserInfo(token, dataConfig.authority);
         return mapSsoUserInfo(data);
      } catch (e) {
         console.error("Userinfo fetch error:", e);
         return null;
      } finally {
         if (!silent) hide();
      }
   }

   useEffect(() => {
      let cancelled = false;

      const fetchUserSignUp = async () => {
         if (!accessToken || !dataConfig?.authority) return;
         const userInfo = await getUserInfoFromToken(accessToken);
         if (!cancelled && userInfo) {
            setUserSignUp(userInfo);
         }
      };

      fetchUserSignUp();

      return () => {
         cancelled = true;
      };
   }, [accessToken, dataConfig?.authority])

   const lang = localStorage.getItem('i18nextLng')
   useEffect(() => {
      let langId = lang === 'en' ? 2 : 1
      localStorage.setItem("i18nextLngId", langId)
      // setCookie("i18nextLng", lang === 'en' ? '2' : '1', {
      //    expires: new Date(Date.now() + 31536000 * 1000), 
      //    path: "/",
      // });
   }, [lang])

   useEffect(() => {
      const token = localStorage.getItem('accessToken');
      if (token) {
         setAccessToken(token);
      }
      setCheckingToken(false);
   }, []);


   useEffect(() => {
      if (userSignUp) {
         setInfoUserSignUp({
            email: userSignUp.email,
            signUpFirstName: userSignUp.firstName,
            signUpLastName: userSignUp.lastName,
            // citizenIdentificationNumber: userSignUp.preferred_username,
            fullName: currentLang !== 'vi'
               ? `${userSignUp.firstName} ${userSignUp.lastName}`
               : `${userSignUp.lastName} ${userSignUp.firstName}`

         });
      }
   }, [userSignUp])

   useEffect(() => {
      const getConfigs = async () => {
         try {
            const { data } = await getConfigsLogin();
            if (data) {
               setDataConfig(data)
            }
         } catch (error) {
            console.log(error);
         }
      };
      getConfigs();
   }, []);

   const refreshProfile = useCallback(() => {
      setRefreshCount((prev) => ++prev);
   }, []);

   const updateProfileOptimistic = useCallback((partialProfile) => {
      if (partialProfile?.companyLogo) {
         skipAccountLogoOverwriteRef.current = true;
      }
      setProfile((prev) => ({
         ...prev,
         ...(partialProfile || {}),
      }));
   }, []);

   const refreshProfileSetting = useCallback(() => {
      setRefreshCountSetting((prev) => ++prev);
   }, []);

   const login = useCallback(
      (accessToken, type = LOGIN_TYPE.normal) => {
         setIsAuthBootstrapping(true);
         setIsAuthenticated(true);
         setAccessToken(accessToken, type);
         refreshProfile();
      },
      [refreshProfile]
   );


   const deleteCookie = () => {
      setCookie("accessToken", "", { "max-age": -1, path: "/" })
      setCookie("refreshToken", "", { "max-age": -1, path: "/" })
   };

   const logout = useCallback(async (options = {}) => {
      try {
         if (!options.silent) {
            show();
         }

         const postLogoutRedirectUri =
            options.postLogoutRedirectUri ||
            `${window.location.origin}/portal/login`;

         const session = JSON.parse(
            localStorage.getItem(dataConfig.key_session_storage) || "{}"
         );
         const idTokenHint = session?.id_token;

         if (session?.profile?.identity_provider_alias === "vneid") {
            auth.signoutRedirect({
               id_token_hint: idTokenHint,
               post_logout_redirect_uri: `https://sso.dancuquocgia.gov.vn/api/idp/sso/login-actions/logout?client_id=sso-mobileId-web&redirect_uri=${encodeURIComponent(
                  postLogoutRedirectUri
               )}`,
            });
         } else {
            auth.signoutRedirect({
               id_token_hint: idTokenHint,
               post_logout_redirect_uri: postLogoutRedirectUri,
            });
         }

         const lang = localStorage.getItem("i18nextLng");
         const langId = localStorage.getItem("i18nextLngId");
         // Giữ activateCode qua logout để quay lại đúng luồng kích hoạt
         const activateCodeKeep = localStorage.getItem("activateCode");

         localStorage.clear();

         if (lang !== null) {
            localStorage.setItem("i18nextLng", lang);
         }

         if (langId !== null) {
            localStorage.setItem("i18nextLngId", langId);
         }

         if (activateCodeKeep) {
            localStorage.setItem("activateCode", activateCodeKeep);
         }

         await auth.removeUser();
         await auth.clearStaleState();

         setProfile(INIT_DATA.profile);
         setIsAuthenticated(INIT_DATA.isAuthenticated);
         setIsAuthBootstrapping(false);
         setIsReady(true);

         lastSyncedAccessTokenRef.current = null;
         clearSessionRefreshExpLock();
         refreshProfile();
         removeAccessToken();
         setHeaderCompanyId();
         deleteCookie();
         setInfoUserSignUp([]);
         setOwnerId(null);
      } catch (error) {
         console.log(error);
      } finally {
         if (!options.silent) {
            hide();
         }
      }
   }, [show, hide, refreshProfile, dataConfig.key_session_storage, dataConfig, clearSessionRefreshExpLock, auth]);

   const logoutNotFoundAccessToken = async () => {
      // Tránh gọi logout nhiều lần khi nhiều API 401 song song (country + userRole, ...)
      if (logoutNotFoundAccessToken._running) return;
      logoutNotFoundAccessToken._running = true;
      try {
         const session = JSON.parse(
            localStorage.getItem(dataConfig.key_session_storage) || "{}"
         );

         if (session?.profile?.identity_provider_alias === "vneid") {
            auth.signoutRedirect({
               id_token_hint: session.id_token,
               post_logout_redirect_uri: `https://sso.dancuquocgia.gov.vn/api/idp/sso/login-actions/logout?client_id=sso-mobileId-web&redirect_uri=${window.location.origin}/portal/login`,
            });
         } else {
            auth.signoutRedirect({
               id_token_hint: session.id_token,
               post_logout_redirect_uri: `${window.location.origin}/portal/login`,
            });
         }

         const lang = localStorage.getItem("i18nextLng");
         const langId = localStorage.getItem("i18nextLngId");

         localStorage.clear();

         if (lang !== null) {
            localStorage.setItem("i18nextLng", lang);
         }

         if (langId !== null) {
            localStorage.setItem("i18nextLngId", langId);
         }

         await auth.removeUser();
         await auth.clearStaleState();

         setProfile(INIT_DATA.profile);
         setIsAuthenticated(INIT_DATA.isAuthenticated);
         setIsAuthBootstrapping(false);
         setIsReady(true);

         lastSyncedAccessTokenRef.current = null;
         clearSessionRefreshExpLock();
         refreshProfile();
         removeAccessToken();
         setHeaderCompanyId();
         // deleteCookie();
         setInfoUserSignUp([]);
         setOwnerId(null);

         navigate("/login");
      } catch (error) {
         console.log(error);
      } finally {
         logoutNotFoundAccessToken._running = false;
      }
   };

   useEffect(() => {
      // Chờ OIDC xử lý callback redirect — tránh kết luận "chưa đăng nhập" quá sớm
      const isOidcSettling = auth?.isLoading || Boolean(auth?.activeNavigator);
      if (isOidcSettling) {
         return;
      }

      const getSessionAccessToken = () => {
         try {
            const storageKey = dataConfig?.key_session_storage;
            if (!storageKey) return null;
            const session = JSON.parse(localStorage.getItem(storageKey) || '{}');
            return session?.access_token || null;
         } catch {
            return null;
         }
      };

      const fetchAccountInfo = async (email, ssoUserInfo) => {
         try {
            const payload = {
               email: email,
               langId: localStorage.getItem("i18nextLngId")

            }
            const applyAccountProfile = (account) => {
               setHeaderCompanyId(account.companyId);
               setProfile((prev) => {
                  const next = { ...account };
                  if (!next.companyLogo && prev?.companyLogo) {
                     next.companyLogo = prev.companyLogo;
                  }
                  if (!next.company && prev?.company) {
                     next.company = prev.company;
                  }
                  if (
                     skipAccountLogoOverwriteRef.current &&
                     prev?.companyLogo &&
                     prev.companyId === next.companyId
                  ) {
                     next.companyLogo = prev.companyLogo;
                     skipAccountLogoOverwriteRef.current = false;
                  }
                  return next;
               });
               setIsAuthenticated(true);
            };

            const goToSignUp = () => {
               setInfoUserSignUp({
                  email: ssoUserInfo?.email,
                  signUpFirstName: ssoUserInfo?.firstName,
                  signUpLastName: ssoUserInfo?.lastName,
               });
               setIsAuthenticated(false);
               navigate('/sign-up', { replace: true });
            };

            if (statusSwitchRef.current) {
               const { data } = await fetchAccount(payload);
               applyAccountProfile(data.data[0]);
            } else {
               // Lấy account theo sub (identityUserId) từ accessToken
               const { data } = await getAccountByIdentity({
                  langId: localStorage.getItem("i18nextLngId"),
               });

               const responseCode = data?.responseCode ?? data?.data?.responseCode;
               const message = String(data?.message || data?.error || '').toLowerCase();
               const isAccountNotFound =
                  String(responseCode) === '1049' ||
                  message.includes('account is not found') ||
                  message.includes('user not found');

               if (isAccountNotFound) {
                  goToSignUp();
                  return;
               }

               const identityAccount = data?.data?.[0] || data?.singleData;
               if (!identityAccount) {
                  goToSignUp();
                  return;
               }

               // Có account (không phải 1049) → lấy danh sách company, chọn theo finalActivity
               const langId = localStorage.getItem("i18nextLngId");
               const companiesResponse = await getAccountList({ langId });
               const companiesPayload = companiesResponse?.data;
               const companies =
                  companiesPayload?.listAccountCompany ||
                  companiesPayload?.data ||
                  [];

               let selectedCompany = null;
               for (let i = 0; i < companies.length; i++) {
                  const company = companies[i];
                  const active = company?.active;
                  if (
                     !company?.companyId ||
                     company?.finalActivity == null ||
                     String(active).toUpperCase() === 'BLOCK' ||
                     String(active).toUpperCase() === 'TERMINATE'
                  ) {
                     continue;
                  }
                  selectedCompany = company;
                  // finalActivity === 1 = công ty đang active trước đó
                  if (Number(company.finalActivity) === 1) {
                     break;
                  }
               }

               if (!selectedCompany?.companyId) {
                  goToSignUp();
                  return;
               }

               setListAccountCompany(companies);
               setHeaderCompanyId(selectedCompany.companyId);

               // Lấy full account (có role) theo company đã chọn — PrivateLayout cần role để gọi getRoleUrlByRoleName
               let fullAccount = identityAccount;
               try {
                  const { data: emailAccountData } = await getAccountInfo({
                     email,
                     langId,
                  });
                  const accountFromEmail =
                     emailAccountData?.data?.[0] ||
                     emailAccountData?.singleData ||
                     emailAccountData?.accountUser;
                  if (accountFromEmail) {
                     fullAccount = accountFromEmail;
                  }
               } catch (emailAccountError) {
                  console.log(
                     'Fallback getAccountInfo after company select:',
                     emailAccountError
                  );
               }

               // Gọi getCompany như luồng cũ trước khi vào dashboard
               try {
                  await getCompany(selectedCompany.companyId, langId);
               } catch (companyDetailError) {
                  console.log('Fetch company detail:', companyDetailError);
               }

               applyAccountProfile({
                  ...fullAccount,
                  companyId: selectedCompany.companyId,
                  company:
                     selectedCompany.companyName ||
                     fullAccount.company ||
                     null,
                  companyLogo:
                     selectedCompany.companyLogo ??
                     fullAccount.companyLogo ??
                     null,
                  userId: selectedCompany.userId ?? fullAccount.userId,
                  permissonId:
                     selectedCompany.permissonId ?? fullAccount.permissonId,
                  accountCompanyId: selectedCompany.accountCompanyId,
                  active: selectedCompany.active,
                  finalActivity: selectedCompany.finalActivity,
                  // Đảm bảo có role để PrivateLayout gọi getRoleUrlByRoleName
                  role: fullAccount.role || identityAccount.role || null,
               });
            }
         } catch (error) {
            if (isAxiosError(error)) {
               console.log('Fetch account info error:', error);
               const data = error.response?.data;
               let message = '';
               let responseCode = null;
               if (typeof data === 'string') {
                  try {
                     const parsed = JSON.parse(data);
                     message = parsed?.message || parsed?.error || data;
                     responseCode = parsed?.responseCode;
                  } catch {
                     message = data;
                  }
               } else if (data && typeof data === 'object') {
                  message = data.message || data.error || '';
                  responseCode = data.responseCode;
               }
               const isAccountNotFound =
                  String(responseCode) === '1049' ||
                  String(message).trim().toLowerCase().includes('account is not found') ||
                  String(message).trim().toLowerCase().includes('user not found');

               if (isAccountNotFound) {
                  setInfoUserSignUp({
                     email: ssoUserInfo?.email,
                     signUpFirstName: ssoUserInfo?.firstName,
                     signUpLastName: ssoUserInfo?.lastName,
                  });

                  setIsAuthenticated(false); // ❗ không cho vào hệ thống
                  // Không gọi logout() — giữ SSO session để đăng ký
                  navigate('/sign-up', { replace: true });
                  return;
               }

               // Hiện đúng message bên thứ 3 trả ra
               const toastMsg =
                  typeof data === 'string'
                     ? message || data
                     : data?.message || data?.error || t('ERRORMSG_0001');
               toast.error(toastMsg);
               logout();
               hide();
            }
         } finally {
            setIsAuthBootstrapping(false);
            setIsReady(true);
            setStatusSwitch(false);
         }
      };

      let cancelled = false;

      const initAccount = async () => {
         const pendingSsoToken =
            getSessionAccessToken() ||
            auth?.user?.access_token ||
            getCookie('accessToken');

         // SSO đã xác thực nhưng token chưa sync — giữ loader, chờ effect login()
         if (!pendingSsoToken) {
            if (!auth?.isAuthenticated) {
               setIsAuthBootstrapping(false);
               setIsReady(true);
            }
            return;
         }

         if (!dataConfig?.authority) {
            return;
         }

         const ssoUserInfo = await getUserInfoFromToken(pendingSsoToken, { silent: true });
         if (cancelled) return;

         const email = ssoUserInfo?.email || auth?.user?.profile?.email;
         if (email) {
            fetchAccountInfo(email, ssoUserInfo);
         } else {
            setIsAuthBootstrapping(false);
            setIsReady(true);
         }
      };

      initAccount();

      return () => {
         cancelled = true;
      };
   }, [
      isNormalLogin,
      refreshCount,
      auth?.isLoading,
      auth?.activeNavigator,
      auth?.isAuthenticated,
      // Không phụ thuộc auth.user — silent renew đổi access_token không cần fetch lại account
      dataConfig?.key_session_storage,
      dataConfig?.authority,
   ]);


   useEffect(() => {
      let cancelled = false;

      const fetchData = async () => {
         try {
            const payload = {
               langId: localStorage.getItem("i18nextLngId")
            };
            const response = await getAccountList(payload);
            if (cancelled) return;

            const accounts =
               response.data.listAccountCompany || response.data.data || [];
            setListAccountCompany(accounts);

            // Company TERMINATE vẫn có finalActivity=1 → BE ưu tiên vào đó.
            // Nếu account hiện tại bị TERMINATE thì tự chuyển sang ACTIVE đầu tiên.
            const currentAccount = accounts.find(
               (item) => String(item.companyId) === String(profile.companyId)
            );
            if (currentAccount?.active !== "TERMINATE") return;

            const firstActive = accounts.find(
               (item) =>
                  item.active === "ACTIVE" &&
                  (item.permissonId === 2 || item.permissonId === 3)
            );
            if (!firstActive || String(firstActive.companyId) === String(profile.companyId)) {
               return;
            }

            const switchResponse = await switchCompany({
               ...firstActive,
               langId: localStorage.getItem("i18nextLngId"),
            });
            if (cancelled) return;

            if (switchResponse.data.code === 200) {
               const code = switchResponse?.data?.data?.responseCode;
               if (code === undefined || code === 0) {
                  setStatusSwitch(true);
                  refreshProfile();
               }
            }
         } catch (error) {
            console.log(error);
         }
      };

      if (accessToken && profile.companyId) {
         fetchData();
      }

      return () => {
         cancelled = true;
      };
   }, [accessToken, isNormalLogin, refreshCount, profile.companyId, refreshProfile, setStatusSwitch]);


   useEffect(() => {
      const fetchData = async () => {
         try {
            const response = await listUserAdmin(profile.companyId);
            // console(response)
         } catch (error) {
            console.log(error);
         }
      };
      if (accessToken && profile.companyId) {
         fetchData();
      }
   }, [accessToken, profile.companyId, refreshCount]);


   useEffect(() => {
      const session = JSON.parse(
         localStorage.getItem(dataConfig?.key_session_storage) || '{}'
      );

      const accessTokenFromSso =
         session?.access_token || auth?.user?.access_token;

      if (auth?.isAuthenticated && accessTokenFromSso) {
         const exp = session?.profile?.exp ?? auth?.user?.profile?.exp;
         const iat = session?.profile?.iat ?? auth?.user?.profile?.iat;

         const tokenLifespanSec = exp && iat ? exp - iat : 3600;
         const tokenLifespanMs = tokenLifespanSec * 1000;
         const expiresAt = new Date(Date.now() + tokenLifespanMs);

         setCookie("accessToken", accessTokenFromSso, {
            expires: expiresAt,
            path: "/",
         });

         setCookie("refreshToken", session?.refresh_token || auth?.user?.refresh_token, {
            expires: expiresAt,
            path: "/",
         });

         // Khóa exp RT lần đầu (nếu có) — renew sau không kéo dài bộ đếm
         try {
            const rt = session?.refresh_token || auth?.user?.refresh_token;
            if (rt) {
               const { exp } = jwtDecode(rt);
               lockSessionRefreshExpMs(exp * 1000);
            }
         } catch {
            // ignore
         }

         // Token chưa đổi so với lần sync trước → bỏ qua
         if (lastSyncedAccessTokenRef.current !== accessTokenFromSso) {
            const alreadyAuthed =
               isAuthenticated && Boolean(getAccessToken());

            lastSyncedAccessTokenRef.current = accessTokenFromSso;

            if (alreadyAuthed) {
               // Silent renew: chỉ cập nhật storage, không bootstrap / remount UI
               setAccessToken(accessTokenFromSso);
            } else {
               // Lần đăng nhập / cold start
               login(accessTokenFromSso);
            }
         }
      }

      const timeout = setTimeout(() => {
         if (
            !auth?.isLoading &&
            !auth?.isAuthenticated &&
            location.pathname === "/"
         ) {
            hide();
            return navigate("/login");
         }
      }, 1000);
      return () => {
         clearTimeout(timeout);
      };
   }, [auth, dataConfig?.key_session_storage, isAuthenticated, login, hide, navigate, location.pathname]);

   const resetInactivityTimer = React.useCallback(() => {
      lastActivityRef.current = Date.now();

      if (inactivityTimerRef.current) {
         clearTimeout(inactivityTimerRef.current);
         inactivityTimerRef.current = null;
      }

      if (
         !isAuthenticated ||
         isPublicAuthRoute(location.pathname) ||
         !isPageVisible()
      ) {
         return;
      }

      inactivityTimerRef.current = setTimeout(() => {
         if (
            isAuthenticated &&
            !isPublicAuthRoute(location.pathname) &&
            isPageVisible()
         ) {
            setShowInactivityDialog(true);
         }
      }, INACTIVITY_MS);
   }, [isAuthenticated, location.pathname]);

   useEffect(() => {
      if (!isAuthenticated || isPublicAuthRoute(location.pathname)) {
         setShowInactivityDialog(false);
         if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
         }
         return;
      }

      const onActivity = () => {
         if (!showInactivityDialog && isPageVisible()) {
            resetInactivityTimer();
         }
      };

      const onVisibilityChange = () => {
         if (document.hidden) {
            if (inactivityTimerRef.current) {
               clearTimeout(inactivityTimerRef.current);
               inactivityTimerRef.current = null;
            }
            return;
         }

         // Quay lại tab: tiếp tục với thời gian còn lại, không đếm lại từ đầu
         if (showInactivityDialog) return;

         const elapsed = Date.now() - lastActivityRef.current;
         const remaining = INACTIVITY_MS - elapsed;

         if (remaining <= 0) {
            setShowInactivityDialog(true);
            return;
         }

         if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
         }
         inactivityTimerRef.current = setTimeout(() => {
            if (
               isAuthenticated &&
               !isPublicAuthRoute(location.pathname) &&
               isPageVisible()
            ) {
               setShowInactivityDialog(true);
            }
         }, remaining);
      };

      const events = ['click', 'keydown', 'mousemove', 'touchstart', 'scroll'];
      events.forEach(evt => window.addEventListener(evt, onActivity, { passive: true }));
      document.addEventListener('visibilitychange', onVisibilityChange);

      if (isPageVisible()) {
         resetInactivityTimer();
      }

      return () => {
         events.forEach(evt => window.removeEventListener(evt, onActivity));
         document.removeEventListener('visibilitychange', onVisibilityChange);
         if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
      };
   }, [isAuthenticated, location.pathname, resetInactivityTimer, showInactivityDialog]);

   const handleInactivityContinue = React.useCallback(() => {
      setShowInactivityDialog(false);
      resetInactivityTimer();
   }, [resetInactivityTimer]);

   const handleInactivityLogout = React.useCallback(() => {
      setShowInactivityDialog(false);
      logoutNotFoundAccessToken(); // hoặc logout();
   }, []);



   return (
      <AuthContext.Provider
         value={{
            profile,
            isNormalLogin,
            isAuthenticated,
            isReady,
            isAdmin,
            isEmployee,
            isAuditor,
            isSubscription,
            login,
            logout,
            refreshProfile,
            updateProfileOptimistic,
            refreshCount,
            genaralSettingDateFormat,
            genaralSettingTimeFormat,
            genaralSettingTimeZone,
            genaralExpiredTime,
            dateTimeFormat,
            infoUserSignUp,
            accessToken,
            dashboardRefreshInterval,
            departmentSatus,
            listAccountCompany,
            setStatusSwitch,
            dataConfig,
            refreshProfileSetting,
            setCookie,
            getCookie,
            defaultReason,
            autoApproveTime,
            weekStartDay,
            leaveTypeDeducted,
            leaveToBeDeducted,
            numberOfHoursOneDay,
            maximumLeaveDuration,
            automaticallyConvertUnexcusedLeaveToExcusedLeave,
            leaveTypeWithoutPermission,
            ownerId
         }}
      >
         {showInactivityDialog && (
            <ConfirmWantToContinueDialog
               open={showInactivityDialog}
               onClose={handleInactivityLogout}
               onConfirm={handleInactivityContinue}
            />
         )}
         {isAuthBootstrapping ? <AppBootstrapLoader /> : children}
      </AuthContext.Provider>
   );
};

export function useAuth() {
   return useContext(AuthContext);
}

export default AuthContext;
